// Visual primitives for the reconciliation module.
//
// Ported from /tmp/erbi-design/erbi/project/primitives.jsx +
// recon-shell.jsx + recon-workspace.jsx.
//
// Prototype-only bits (Babel/UMD wrapper, in-page Tweaks panel, state
// preview controls) are stripped per SPEC §5 / §8.

import React from 'react';
import {
  TOKENS,
  UI_FONT,
  MONO_FONT,
  NUM_STYLE,
  laneColor,
  laneSoft,
  SAP_CATEGORIES,
  fmtMoney,
  confTier,
} from './tokens';

// ─── Icon ────────────────────────────────────────────────────────────
export function Icon({ d, size = 14, stroke = 'currentColor', fill = 'none', sw = 1.6 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: '0 0 auto' }}
    >
      <path d={d} />
    </svg>
  );
}

// ─── Card ────────────────────────────────────────────────────────────
export function Card({ children, padding = 14, accent, style }) {
  return (
    <div
      style={{
        background: TOKENS.panel,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 10,
        padding,
        borderLeft: accent ? `3px solid ${accent}` : `1px solid ${TOKENS.border}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Mono ────────────────────────────────────────────────────────────
export function Mono({ children, size = 11.5, weight = 500, color = TOKENS.ink, style }) {
  return (
    <span
      style={{
        ...NUM_STYLE,
        fontSize: size,
        fontWeight: weight,
        color,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ─── SectionLabel ────────────────────────────────────────────────────
export function SectionLabel({ children, extra }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          fontFamily: MONO_FONT,
          fontSize: 10,
          color: TOKENS.ink3,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </span>
      {extra && <span style={{ marginLeft: 'auto' }}>{extra}</span>}
    </div>
  );
}

// ─── SelectBox (checkbox) ───────────────────────────────────────────
export function SelectBox({ checked, onChange, size = 14 }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      style={{
        width: size,
        height: size,
        borderRadius: 3,
        flex: '0 0 auto',
        background: checked ? TOKENS.select : TOKENS.panel,
        border: `1.5px solid ${checked ? TOKENS.select : TOKENS.borderStrong}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        cursor: 'pointer',
        transition: 'all .12s',
      }}
    >
      {checked && (
        <svg
          width={size - 4}
          height={size - 4}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12l4 4 10-10" />
        </svg>
      )}
    </span>
  );
}

// ─── Tone pill ──────────────────────────────────────────────────────
export function Tone({ tone, children, icon, title }) {
  const map = {
    good: { c: TOKENS.ok, bg: 'oklch(0.96 0.04 150)' },
    soft: { c: TOKENS.warn, bg: 'oklch(0.96 0.04 70)' },
    miss: { c: TOKENS.danger, bg: 'oklch(0.96 0.04 25)' },
    info: { c: TOKENS.ink2, bg: TOKENS.panelAlt },
  };
  const t = map[tone] || map.info;
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: UI_FONT,
        fontSize: 10.5,
        fontWeight: 600,
        color: t.c,
        background: t.bg,
        border: `1px solid ${t.c}`,
        padding: '1px 6px',
        borderRadius: 9,
        whiteSpace: 'nowrap',
        cursor: title ? 'help' : undefined,
      }}
    >
      {icon}
      {children}
    </span>
  );
}

// ─── Money cell ─────────────────────────────────────────────────────
export function MoneyCell({ value, currency = 'EUR', size = 12, color }) {
  return (
    <span
      style={{
        ...NUM_STYLE,
        fontSize: size,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        color: color || (value < 0 ? TOKENS.danger : TOKENS.ink),
      }}
    >
      {fmtMoney(value, currency)}
    </span>
  );
}

// ─── Category tag ───────────────────────────────────────────────────
export function CategoryTag({ id }) {
  const c = SAP_CATEGORIES.find((x) => x.id === id);
  if (!c) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: MONO_FONT,
        fontSize: 9,
        fontWeight: 600,
        color: laneColor(c.hue, 0.38, 0.12),
        background: laneSoft(c.hue),
        border: `1px solid ${laneColor(c.hue, 0.86, 0.07)}`,
        padding: '1px 6px',
        borderRadius: 3,
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}
    >
      {c.name.toUpperCase()}
    </span>
  );
}

// ─── Avatar ─────────────────────────────────────────────────────────
export function Avatar({ a, size = 18 }) {
  if (!a) return null;
  const hue = a.hue ?? 220;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `oklch(0.92 0.04 ${hue})`,
        color: `oklch(0.32 0.10 ${hue})`,
        fontSize: size * 0.46,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto',
        letterSpacing: '-0.02em',
      }}
    >
      {a.initials}
    </div>
  );
}

// ─── Status pill ────────────────────────────────────────────────────
export function StatusPill({ status }) {
  const map = {
    in_progress: { color: TOKENS.warn,   bg: 'oklch(0.96 0.04 70)',   label: 'In progress' },
    completed:   { color: TOKENS.ok,     bg: 'oklch(0.96 0.04 150)',  label: 'Completed'   },
    processed:   { color: TOKENS.accent, bg: TOKENS.accentSoft,       label: 'Processed'   },
    pending:     { color: TOKENS.ink3,   bg: TOKENS.panelAlt,         label: 'Pending'     },
    queued:      { color: TOKENS.ink3,   bg: TOKENS.panelAlt,         label: 'Queued'      },
    failed:      { color: TOKENS.danger, bg: 'oklch(0.96 0.04 25)',   label: 'Failed'      },
    error:       { color: TOKENS.danger, bg: 'oklch(0.96 0.04 25)',   label: 'Error'       },
  };
  const s = map[status] || map.queued;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontFamily: UI_FONT,
        fontSize: 10.5,
        fontWeight: 600,
        color: s.color,
        background: s.bg,
        border: `1px solid ${s.color}`,
        padding: '1px 7px',
        borderRadius: 10,
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color }} />
      {s.label}
    </span>
  );
}

// ─── Confidence chip ────────────────────────────────────────────────
export function ConfidenceChip({ value, size = 'sm' }) {
  // value: 0..1
  const pct = Math.round((value ?? 0) * 100);
  const tier = confTier(value ?? 0);
  const color = tier === 'good' ? TOKENS.ok : tier === 'soft' ? TOKENS.warn : TOKENS.danger;
  const bg =
    tier === 'good'
      ? 'oklch(0.96 0.04 150)'
      : tier === 'soft'
      ? 'oklch(0.96 0.04 70)'
      : 'oklch(0.96 0.04 25)';
  const pad = size === 'lg' ? '3px 9px' : '1px 6px';
  const fs = size === 'lg' ? 12 : 10.5;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        ...NUM_STYLE,
        fontSize: fs,
        fontWeight: 600,
        color,
        background: bg,
        border: `1px solid ${color}`,
        padding: pad,
        borderRadius: 4,
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: color }} />
      {pct}
    </span>
  );
}

// ─── Modal (shared shell) ───────────────────────────────────────────
export function Modal({ title, subtitle, children, onClose, wide, footer, overflowVisible }) {
  // Close on ESC.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'oklch(0.2 0.01 260 / 0.45)',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: wide ? 660 : 520,
          maxHeight: 'calc(100vh - 80px)',
          background: TOKENS.panel,
          borderRadius: 12,
          border: `1px solid ${TOKENS.border}`,
          boxShadow: '0 32px 80px rgba(15,18,30,0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: overflowVisible ? 'visible' : 'hidden',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: `1px solid ${TOKENS.border}`,
            display: 'flex',
            alignItems: 'center',
            flex: '0 0 auto',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: UI_FONT, fontSize: 14, fontWeight: 600 }}>
              {title}
            </div>
            {subtitle && (
              <Mono size={10.5} color={TOKENS.ink3}>
                {subtitle}
              </Mono>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              padding: '5px 8px',
              borderRadius: 6,
              border: `1px solid ${TOKENS.border}`,
              background: TOKENS.panel,
              color: TOKENS.ink2,
              cursor: 'pointer',
            }}
          >
            <Icon d="M6 6l12 12M18 6L6 18" size={12} sw={2} />
          </button>
        </div>
        <div style={{ padding: 18, overflow: overflowVisible ? 'visible' : 'auto', flex: '1 1 auto' }}>
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: '12px 18px',
              borderTop: `1px solid ${TOKENS.border}`,
              background: TOKENS.panelAlt,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flex: '0 0 auto',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Toast (fixed bottom centre, auto-dismiss) ──────────────────────
export function Toast({ text, tone = 'good', onDismiss, duration = 3200 }) {
  React.useEffect(() => {
    if (!onDismiss) return;
    const t = setTimeout(onDismiss, duration);
    return () => clearTimeout(t);
  }, [onDismiss, duration]);
  const bg = tone === 'good' ? TOKENS.ok : tone === 'warn' ? TOKENS.warn : TOKENS.danger;
  const fg = tone === 'good' ? '#062015' : tone === 'warn' ? '#211504' : '#fff';
  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 78,
        transform: 'translateX(-50%)',
        zIndex: 90,
        background: bg,
        color: fg,
        borderRadius: 10,
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: UI_FONT,
        fontSize: 12,
        fontWeight: 600,
        boxShadow: '0 18px 48px rgba(10,40,25,0.35)',
      }}
    >
      <Icon d="M5 12l4 4 10-10" size={13} sw={2.5} />
      {text}
    </div>
  );
}

// ─── Stat pill (one cell of the 7-cell stat strip) ──────────────────
export function StatPill({ label, value, hue, extra, color, small }) {
  return (
    <div
      style={{
        flex: 1,
        padding: '6px 12px',
        minWidth: 0,
        borderLeft: `2px solid ${laneColor(hue, 0.78, 0.12)}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <span
        style={{
          fontFamily: MONO_FONT,
          fontSize: 9,
          color: TOKENS.ink3,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span
          style={{
            ...NUM_STYLE,
            fontSize: small ? 12 : 17,
            fontWeight: 500,
            color: color || TOKENS.ink,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </span>
        {extra && (
          <span style={{ fontFamily: MONO_FONT, fontSize: 10, color: TOKENS.ink3 }}>
            {extra}
          </span>
        )}
      </span>
    </div>
  );
}

// ─── Count chip (used in the dark action bar) ───────────────────────
export function CountChip({ n, label, hue }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 8px',
        borderRadius: 6,
        background: 'oklch(0.24 0.012 260)',
        border: `1px solid ${laneColor(hue, 0.55, 0.13)}`,
        color: '#fff',
        fontFamily: UI_FONT,
        fontSize: 11.5,
        fontWeight: 600,
      }}
    >
      <span
        style={{
          ...NUM_STYLE,
          fontSize: 13,
          color: laneColor(hue, 0.78, 0.12),
        }}
      >
        {n}
      </span>
      <span
        style={{
          fontFamily: MONO_FONT,
          fontSize: 9.5,
          letterSpacing: '0.06em',
          color: laneColor(hue, 0.85, 0.07),
        }}
      >
        {label}
      </span>
    </span>
  );
}

// ─── Sum check (= / ≈ / ≠) ──────────────────────────────────────────
export function SumCheck({ sapTotal, invTotal }) {
  const delta = invTotal - sapTotal;
  const abs = Math.abs(delta);
  const max = Math.max(Math.abs(sapTotal), Math.abs(invTotal));
  const ratio = max > 0 ? abs / max : 0;
  const tone = abs < 0.01 ? 'good' : ratio < 0.01 ? 'soft' : 'miss';
  const sign = tone === 'good' ? '=' : tone === 'soft' ? '≈' : '≠';
  const color = tone === 'good' ? TOKENS.ok : tone === 'soft' ? TOKENS.warn : TOKENS.danger;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        borderRadius: 7,
        background: 'oklch(0.24 0.012 260)',
        border: '1px solid oklch(0.32 0.012 260)',
        ...NUM_STYLE,
        fontSize: 11.5,
        color: '#fff',
      }}
    >
      <span style={{ color: 'oklch(0.72 0.01 250)', fontSize: 9.5, letterSpacing: '0.06em' }}>
        SAP
      </span>
      <span>{fmtMoney(sapTotal)}</span>
      <span style={{ color, fontWeight: 700, fontSize: 13 }}>{sign}</span>
      <span style={{ color: 'oklch(0.72 0.01 250)', fontSize: 9.5, letterSpacing: '0.06em' }}>
        INV
      </span>
      <span>{fmtMoney(invTotal)}</span>
      {abs > 0.01 && (
        <span style={{ color, marginLeft: 6, fontSize: 10.5 }}>Δ {fmtMoney(delta)}</span>
      )}
    </div>
  );
}

// ─── Shared button styles (exported so views can reuse) ─────────────
export const styles = {
  primaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    background: TOKENS.ink,
    color: TOKENS.headerInk,
    fontFamily: UI_FONT,
    fontSize: 11.5,
    fontWeight: 550,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  primaryBtnLg: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 7,
    border: 'none',
    background: TOKENS.ink,
    color: TOKENS.headerInk,
    fontFamily: UI_FONT,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  ghostBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '6px 12px',
    borderRadius: 6,
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.panel,
    color: TOKENS.ink2,
    fontFamily: UI_FONT,
    fontSize: 11.5,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  ghostDarkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid oklch(0.32 0.012 260)',
    background: 'transparent',
    color: 'oklch(0.84 0.006 250)',
    fontFamily: UI_FONT,
    fontSize: 11.5,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  primaryBigBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    borderRadius: 7,
    border: 'none',
    background: TOKENS.ok,
    color: '#062015',
    fontFamily: UI_FONT,
    fontSize: 12.5,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  tableBase: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: UI_FONT,
    marginTop: 12,
  },
  thStyle: {
    padding: '8px 12px',
    textAlign: 'left',
    fontFamily: MONO_FONT,
    fontSize: 9.5,
    color: TOKENS.ink3,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontWeight: 600,
    borderBottom: `1px solid ${TOKENS.border}`,
    background: TOKENS.panelAlt,
  },
  tdStyle: {
    padding: '8px 12px',
    borderBottom: `1px solid ${TOKENS.border}`,
    fontFamily: UI_FONT,
    fontSize: 11.5,
    color: TOKENS.ink,
    verticalAlign: 'middle',
  },
};
